import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Box, InputBase, IconButton, Badge, Drawer, List,
  ListItem, ListItemButton, ListItemText, Menu, MenuItem,
  Dialog, DialogContent, DialogActions, Typography, Button,
  useMediaQuery
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  AccountCircleOutlined as AccountCircleOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link, useNavigate, createSearchParams } from 'react-router-dom';
import Logo from '../assets/images/logo.png';
import { useSelector } from 'react-redux';
import { getUser, logoutUser } from '../utils/auth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // ⬅️ Add this import at the top


// Styled Search
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  width: '100%',
  maxWidth: 300,
}));
const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%', position: 'absolute',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  pointerEvents: 'none',
}));
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#555',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
    '::placeholder': { color: '#555', opacity: 1 },
  },
}));

export default function Header() {
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const searchRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const cartItems = useSelector(state => state.cart.cartItems);
  const user = getUser();

  const handleSearchSubmit = () => {
    if (searchTerm.trim()) {
      navigate({
        pathname: '/products',
        search: `?${createSearchParams({ search: searchTerm.trim() })}`,
      });
      setSearchTerm('');
      setMobileSearchOpen(false);
    }
  };

  // Auto-close mobile search when tapping outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (mobileSearchOpen && searchRef.current && !searchRef.current.contains(e.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileSearchOpen]);

  const handleProfileMenuOpen = e => setProfileAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);
  const handleMyAccount = () => { handleProfileMenuClose(); navigate('/myaccount'); };
  const handleLogout = () => { handleProfileMenuClose(); setLogoutDialogOpen(true); };
  const confirmLogout = () => {
    logoutUser(); setLogoutDialogOpen(false);
    navigate('/login'); window.location.reload();
  };

  return (
    <>
      <AppBar position="sticky" sx={{
        background: '#ffd6dd',
        boxShadow: 'none',
        px: { xs: 1, md: 10 },
        py: 1,
        [theme.breakpoints.up('md')]: { background: 'radial-gradient(#ffd6d0,#ffd6d6)' },
      }}>
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          {isMobile
            ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}>
                <IconButton onClick={() => setDrawerOpen(true)}>
                  <MenuIcon sx={{ color: '#555' }} />
                </IconButton>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={Logo} alt="Logo"
                      style={{ height: '30px', objectFit: 'contain' }} />
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!mobileSearchOpen && (
                    <IconButton onClick={() => setMobileSearchOpen(true)}>
                      <SearchIcon sx={{ color: '#555' }} />
                    </IconButton>
                  )}
                  <IconButton onClick={() => navigate('/cart')}>
                    <Badge badgeContent={cartItems.length} color="error">
                      <ShoppingCartIcon sx={{ color: '#555' }} />
                    </Badge>
                  </IconButton>
                </Box>
              </Box>
            )
            : (
              <>
                <Link to="/">
                  <img src={Logo} alt="Logo"
                    style={{ height: '40px', objectFit: 'contain' }} />
                </Link>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {['Home', 'Products', 'About Us', 'Contact Us'].map(label => (
                    <Button
                      key={label}
                      component={Link}
                      to={
                        label === 'Home'
                          ? '/'
                          : `/${label.toLowerCase().replace(/ /g, '')}`
                      }
                      sx={{
                        color: '#555',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': { color: 'black' }
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Search>
                    <SearchIconWrapper>
                      <SearchIcon sx={{ color: '#555' }} />
                    </SearchIconWrapper>
                    <StyledInputBase
                      placeholder="Search…"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
                    />
                  </Search>
                  <IconButton onClick={() => navigate('/cart')}>
                    <Badge badgeContent={cartItems.length} color="error">
                      <ShoppingCartIcon sx={{ color: '#555' }} />
                    </Badge>
                  </IconButton>
                  {user
                    ? <IconButton onClick={handleProfileMenuOpen}>
                      <AccountCircleOutlinedIcon sx={{ color: '#555' }} />
                    </IconButton>
                    : <>
                      <Button onClick={() => navigate('/login')}
                        sx={{ fontSize: '13px', textTransform: 'none', color: '#555' }}>Login</Button>
                      <Button onClick={() => navigate('/register')}
                        sx={{ fontSize: '13px', textTransform: 'none', color: '#555' }}>Register</Button>
                    </>
                  }
                </Box>
              </>
            )}
        </Toolbar>
        {isMobile && mobileSearchOpen && (
  <Box sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1200,
    bgcolor: 'rgba(255, 255, 255, 0.25)', // slight light overlay
    backdropFilter: 'blur(8px)', // main blur effect for homepage
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 0.25s ease-in-out',
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  }}>
    <Box sx={{
      bgcolor: 'radial-gradient(#fff, #ffd6d6)',
      px: 2,
      py: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    }}>
      <IconButton
        onClick={() => setMobileSearchOpen(false)}
        sx={{
          backgroundColor: 'rgba(255, 214, 214, 0.7)',
        backdropFilter: 'blur(4px)',
          borderRadius: 2,
          p: 1,
          boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.1)'
        }}
      >
        <ArrowBackIcon sx={{ color: '#555' }} />
      </IconButton>

      <Box sx={{
        flex: 1,
        backgroundColor: 'rgba(255, 214, 214, 0.7)',
        backdropFilter: 'blur(4px)',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.8,
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
      }}>
        {/* <SearchIcon sx={{
          color: '#555',
          backgroundColor: '#ffebeb',
          borderRadius: '50%',
          padding: '6px',
          fontSize: '20px',
          '&:hover': {
            backgroundColor: '#ffb3b3',
          }
        }} /> */}
        <InputBase
          autoFocus
          fullWidth
          placeholder='Try searching “T-shirts”'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
          sx={{
            fontSize: '14px',
            color: '#333',
            ml: 1
          }}
        />
      </Box>
    </Box>

    {/* Live Filtered Suggestions */}
    {searchTerm.trim() && (
      <Box sx={{
        flex: 1,
        px: 2,
        py: 1,
        overflowY: 'auto',
        backgroundColor: 'rgba(255, 214, 214, 0.25)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        borderTop: '1px solid #f7cfcf',
      }}>
        {['Nike Shoes', 'Nike T-Shirts', 'Nike Shorts', 'Nike Caps']
          .filter(item => item.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((product, idx) => (
            <Box
              key={idx}
              onClick={() => {
                navigate({
                  pathname: '/products',
                  search: `?search=${product}`,
                });
                setMobileSearchOpen(false);
                setSearchTerm('');
              }}
              sx={{
                px: 2,
                py: 1,
                borderBottom: '1px solid #f5caca',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#ffe3e3' }
              }}
            >
              <Typography fontSize={14} color="#333">
                {product}
              </Typography>
            </Box>
          ))}
      </Box>
    )}
  </Box>
)}



        {/* {isMobile && mobileSearchOpen && (
          <Box ref={searchRef} sx={{
            position: 'fixed', top: 0, left: 0, zIndex: 1200,
            width: '100vw', height: '100vh', bgcolor: '#fff',
            p: 2, display: 'flex', flexDirection: 'column', gap: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box></Box>
              <IconButton onClick={() => setMobileSearchOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Search sx={{ width: '100%' }}>
              <SearchIconWrapper>
                <SearchIcon sx={{ color: '#555' }} />
              </SearchIconWrapper>
              <StyledInputBase
                autoFocus placeholder="Search…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
              />
            </Search>
          </Box> */}
        {/* )} */}
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { background: 'radial-gradient(#fff,#ffd6d6)', width: 250 } }}>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ height: '70px', display: 'flex', alignItems: 'center', pl: 4, mb: 2 }}>
            <img src={Logo} alt="Logo" height="35px" />
          </Box>
          <List>
            {['Home', 'Products', 'About Us', 'Contact Us'].map(label => (
              <ListItem key={label} disablePadding>
                <ListItemButton component={Link}
                  to={`/${label.toLowerCase().replace(/ /g, '')}`}
                  onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary={label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Menu anchorEl={profileAnchorEl} open={isProfileMenuOpen} onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: 1, minWidth: 100, bgcolor: '#fff4f0',
            borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              fontSize: '14px', fontWeight: 500,
              justifyContent: 'center', color: '#333',
              '&:hover': { bgcolor: '#ffe2db' }
            },
          }
        }} disableScrollLock>
        <MenuItem onClick={handleMyAccount}>My Account</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#ffeaea', px: 2, py: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <img src="https://cdn-icons-png.flaticon.com/512/1828/1828665.png"
              alt="Warning"
              width={24} height={24} />
            <Typography variant="h6" fontWeight="bold" color="#b71c1c">Confirm Logout</Typography>
          </Box>
        </Box>
        <DialogContent>
          <Typography fontSize={15} fontWeight={500}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)}
            variant="outlined" sx={{ textTransform: 'none', color: '#555', borderColor: '#ccc' }}>
            Cancel
          </Button>
          <Button onClick={confirmLogout}
            variant="contained" sx={{ textTransform: 'none', backgroundColor: '#ff523b', '&:hover': { backgroundColor: '#e53935' } }}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
